import React, { useEffect, useState, useMemo } from 'react';
import moment, { Moment } from 'moment-timezone';
import { useCallRequestMutation, useIsEverybodyAvailable } from '../../../hooks/api/useCalls';
import Toast from '../../common/pop-up-message';
import { useQueryClient } from '@tanstack/react-query';
import { useHistory } from 'react-router-dom';
import { useLockBody } from '../../../hooks/utils/useLockBody';
import { twMerge } from 'tailwind-merge';
import { HorizontalScrollSection } from '../../common/horizontal-scroll';
import useAuth from '../../../hooks/utils/useAuth';
import { useSendKeycloakVerificationCode, useVerifyKeycloakVerificationCode } from '../../../hooks/api/useUser';
import { formatPhoneNumber } from '../../../utils/constants';
import { OtpVerification } from '../../common/otp-verification';
import { Availability, peersQueryKeys } from '../../../hooks/api/usePeers';
import { TopicFilterDropdown } from '../../topic-filter-dropdown';
import { Avatar } from '../../avatar';
import { useTagGroups } from '../../../hooks/api/useTagGroups';
import rollbar from '../../../utils/rollbar';
import OutgoingCallIcon from '../../../assets/images/svgs/outgoing-call-26.svg';
import { DrawerModal } from '../drawer-modal';
import { useSettingsModal, SettingsPage } from '../../../contexts/settings-modal-context';
import {
  canMemberScheduleCall,
  MIN_SCHEDULING_MINUTES,
  type SchedulingBlockReason,
} from '../../../utils/scheduling-eligibility';

// default pre-configured length of a call units call in minutes
// keep in sync with the legacy scheduling modal implementation
const CALL_UNITS_CALL_DEFAULT_LENGTH = 30;

enum SchedulingModalStep {
  NOT_ENOUGH_MINUTES,
  TIME_STEP,
  FEELING_STEP,
  PHONE_NUMBER_STEP,
  OTP_STEP,
}

interface ScheduleData {
  start: Moment;
  end: Moment;
}

interface Schedule {
  data: ScheduleData[];
  dateLabel: string;
}

const createTimeSplits = (start: Moment, end: Moment): ScheduleData[] => {
  const times = [];

  let currentTime = start;

  while (currentTime.isBefore(end)) {
    times.push({
      start: moment(currentTime),
      // this is a weird moment thing, but since moment's objects are mutable, it's changing current time during this line as well
      end: moment(currentTime.add(30, 'm')),
    });
  }

  return times;
};

export const SchedulingModal: React.FC<{
  open: boolean;
  onExit: (context?: 'scheduled' | 'cancelled' | null, callRequestData?: any) => void;
  listenerId: number;
  listenerAvailableNow: boolean;
  availabilities: Availability[];
  listenerName: string;
  listenerImage?: string;
  originalRequestId?: number;
  location: string;
  startOnFeelingStep?: 'Now' | 'Time' | undefined;
  initialTime?: Moment;
  initialTagGroupId?: number;
  initialText?: string;
}> = ({
  onExit,
  listenerId,
  listenerAvailableNow,
  availabilities,
  listenerName,
  listenerImage,
  originalRequestId: original_request_id = null,
  location,
  open,
  startOnFeelingStep = undefined,
  initialTime,
  initialTagGroupId,
  initialText,
}) => {
  useLockBody(open);
  const now = moment();
  const history = useHistory();

  const { user } = useAuth();
  const scheduleCallRequestMutation = useCallRequestMutation();
  const queryClient = useQueryClient();
  const isNow = useIsEverybodyAvailable(listenerAvailableNow);
  const { isModalOpen: isSettingsModalOpen } = useSettingsModal();
  const schedulingEligibility = useMemo(() => canMemberScheduleCall(user), [user]);

  const getStepAfterPhoneVerification = () => {
    if (!schedulingEligibility.canSchedule && schedulingEligibility.reason !== 'loading') {
      return SchedulingModalStep.NOT_ENOUGH_MINUTES;
    }
    if (startOnFeelingStep === 'Now' || startOnFeelingStep === 'Time') {
      return SchedulingModalStep.FEELING_STEP;
    }
    return SchedulingModalStep.TIME_STEP;
  };

  // Determine initial step based on MFA requirement and phone verification status
  const getInitialStep = () => {
    if (!user?.mobile_phone_verified || !user?.mobile_phone) {
      return SchedulingModalStep.PHONE_NUMBER_STEP;
    }
    return getStepAfterPhoneVerification();
  };

  const [step, setStep] = useState(getInitialStep());
  const [time, setTime] = useState<Moment | undefined>(undefined);
  const [date, setDate] = useState<number | undefined>(undefined);
  const [moodScore, setMoodScore] = useState<number | null>(null);
  const [selectedTagGroupId, setSelectedTagGroupId] = useState<number | undefined>(initialTagGroupId);
  const [optionalText, setOptionalText] = useState<string>(initialText || '');
  const [phoneNumber, setPhoneNumber] = useState(formatPhoneNumber(user?.mobile_phone?.replace('+1', '') || ''));
  const [phoneNumberError, setPhoneNumberError] = useState<string | null>(null);
  const [phoneNumberIsSubmitting, setPhoneNumberIsSubmitting] = useState<boolean>(false);
  const { openModal: openSettingsModal } = useSettingsModal();

  const userTimeZone = user?.timezone ? user.timezone : Intl.DateTimeFormat().resolvedOptions().timeZone;
  const nowInUserTimeZone = moment().tz(userTimeZone);
  const availableMinutes = user?.onUnlimitedPlan ? undefined : user?.caller_role?.payment_data?.available_minutes;
  const schedulingBlockReason: SchedulingBlockReason | undefined = schedulingEligibility.canSchedule
    ? undefined
    : schedulingEligibility.reason;

  useEffect(() => {
    if (user?.mobile_phone_verified && step === SchedulingModalStep.OTP_STEP) {
      setStep(getStepAfterPhoneVerification());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.mobile_phone_verified, step, startOnFeelingStep, schedulingEligibility]);

  // Refetch user data when settings modal closes (in case payment method was added)
  useEffect(() => {
    if (!isSettingsModalOpen && open) {
      // Settings modal just closed, refetch user data to get updated payment method
      // Use refetch instead of just invalidate to ensure we get fresh data immediately
      queryClient.refetchQueries(['me']).catch(() => {
        // Fallback to invalidate if refetch fails
        queryClient.invalidateQueries(['me']);
      });
    }
  }, [isSettingsModalOpen, open, queryClient]);

  const sendRequest = async () => {
    if (!schedulingEligibility.canSchedule) {
      if (schedulingEligibility.reason === 'payment_failed') {
        Toast.error(
          'There was an error processing your last payment. Please update your payment method prior to scheduling a new call.',
        );
      } else if (schedulingEligibility.reason === 'no_credit') {
        Toast.error(
          `You need at least ${MIN_SCHEDULING_MINUTES} minutes of credit or a payment method on file to schedule a call.`,
        );
      }
      setStep(SchedulingModalStep.NOT_ENOUGH_MINUTES);
      return;
    }

    if (user && selectedTagGroupId && time && date && moodScore !== null) {
      try {
        // For now calls, scheduled_at should be undefined. Compare with current time in user's timezone
        const currentTimeInUserTz = moment().tz(userTimeZone);
        const isNowCall = time.isSameOrBefore(currentTimeInUserTz);
        const response = await scheduleCallRequestMutation.mutateAsync(
          {
            listenerRoleId: listenerId,
            payload: {
              call_units_length: user.caller_role.is_call_units ? CALL_UNITS_CALL_DEFAULT_LENGTH : null,
              caller_role_id: user.caller_role_id,
              original_request_id: original_request_id,
              perform_backup_requests: true,
              scheduled_at: isNowCall ? undefined : time.toISOString(),
              tag_group_ids: [selectedTagGroupId],
              text: optionalText || undefined,
              begin_call_mood_score: moodScore,
            },
            location: location,
            peerName: listenerName,
            listenerImage: listenerImage,
          },
          {
            onSuccess: () => {
              queryClient.invalidateQueries(['callRequests']);
              Toast.success('Call created.');
            },
          },
        );
        // Pass the response data along with metadata to onExit so parent can open call details modal
        onExit('scheduled', {
          ...response.response,
          _isNowCall: isNowCall,
          _listenerName: listenerName,
          _listenerId: listenerId,
          _listenerImage: listenerImage,
        });
      } catch (errors: any) {
        if (errors.response?.data?.description) {
          Toast.error(errors.response?.data.description);
        } else {
          queryClient.invalidateQueries(['callRequests']);
          rollbar.error(errors.response?.data.description);
          Toast.error(
            'Oops. It looks like we ran into an issue verifying your schedule. We will work to resolve this shortly, please try back in a little while.',
          );
        }
      }
    } else {
      if (!selectedTagGroupId) {
        Toast.error('Please select a topic.');
      } else if (!time || !date) {
        Toast.error('Please select a day and time.');
      } else if (moodScore === null) {
        Toast.error('Please select how you are feeling (1–10).');
      }
    }
  };

  const schedule: Map<number, Schedule> | undefined = useMemo(() => {
    if (!availabilities || availabilities.length === 0) {
      return undefined;
    }

    const sch = availabilities.reduce((sch, current) => {
      // Parse start_date and end_date as UTC first, then convert to user's timezone
      // The start_date/end_date are UTC timestamps (e.g., '2026-01-28T09:00:00+00:00')
      const startDate = moment.utc(current.start_date).tz(userTimeZone);
      const endDate = moment.utc(current.end_date).tz(userTimeZone);

      if (endDate.isBefore(moment())) {
        return sch;
      }

      // setting the key as the day of the year to make sorting easy
      const key: number = startDate.dayOfYear();
      const timeSplits = createTimeSplits(startDate, endDate);

      if (!sch.get(key)) {
        sch.set(key, {
          dateLabel:
            startDate.dayOfYear() === now.dayOfYear()
              ? `Today, ${moment().format('ddd')}`
              : startDate.format('MMM D, ddd'),
          data: timeSplits,
        });
      } else {
        sch.get(key)?.data.push(...timeSplits);
      }

      return sch;
    }, new Map<number, Schedule>());

    // If we have an initialTime (for rescheduling), add it to the schedule if it doesn't already exist
    // This ensures the currently scheduled time appears as an option and can be highlighted
    if (initialTime && sch) {
      const initialTimeDayOfYear = initialTime.dayOfYear();
      const daySchedule = sch.get(initialTimeDayOfYear);

      // Check if the initialTime already exists in the schedule
      const timeSlotExists = daySchedule?.data.some((slot) => slot.start.isSame(initialTime, 'minute'));

      if (!timeSlotExists) {
        // Calculate the end time (assuming 30-minute call duration)
        const initialTimeEnd = initialTime.clone().add(30, 'minutes');
        const initialTimeSlot: ScheduleData = {
          start: initialTime.clone(),
          end: initialTimeEnd,
        };

        if (daySchedule) {
          // Add to existing day
          daySchedule.data.push(initialTimeSlot);
          // Sort the slots by time
          daySchedule.data.sort((a, b) => (a.start.isSameOrBefore(b.start) ? -1 : 1));
        } else {
          // Create new day entry
          const dateLabel =
            initialTimeDayOfYear === now.dayOfYear()
              ? `Today, ${moment().format('ddd')}`
              : initialTime.format('MMM D, ddd');
          sch.set(initialTimeDayOfYear, {
            dateLabel,
            data: [initialTimeSlot],
          });
        }
      }
    }

    // make sure to add now date and time to map if isNow
    if (isNow && sch) {
      if (!sch.has(nowInUserTimeZone.dayOfYear())) {
        sch.set(nowInUserTimeZone.dayOfYear(), {
          dateLabel: `Today, ${nowInUserTimeZone.format('ddd')}`,
          data: [
            {
              end: nowInUserTimeZone,
              start: nowInUserTimeZone,
            },
          ],
        });
      } else {
        sch.get(nowInUserTimeZone.dayOfYear())?.data.push({
          end: nowInUserTimeZone,
          start: nowInUserTimeZone,
        });
      }
    }

    return sch;
  }, [availabilities, userTimeZone, now, isNow, nowInUserTimeZone, initialTime]);

  useEffect(() => {
    // Initialize time/date only when availability state (isNow) changes,
    // to avoid recreating moments and triggering an infinite update loop.
    // If starting on feeling step with 'Now' (Connect Now), set time/date to now immediately
    // If initialTime is provided, use that time (for next available time scenario)
    if (startOnFeelingStep === 'Now') {
      setTime(nowInUserTimeZone);
      setDate(nowInUserTimeZone.dayOfYear());
    } else if (initialTime) {
      // When we have initialTime, set it (it will be added to the schedule if it doesn't exist)
      // This ensures the currently scheduled time appears and can be highlighted when rescheduling
      setTime(initialTime);
      setDate(initialTime.dayOfYear());
    } else {
      setTime(isNow ? nowInUserTimeZone : undefined);
      setDate(isNow ? nowInUserTimeZone.dayOfYear() : undefined);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isNow, startOnFeelingStep, initialTime]);

  // Initialize form fields when modal opens with initial values
  useEffect(() => {
    if (open) {
      setSelectedTagGroupId(initialTagGroupId);
      setOptionalText(initialText || '');
    } else {
      // Reset form fields when modal closes
      setSelectedTagGroupId(undefined);
      setOptionalText('');
      setMoodScore(null);
      setPhoneNumberError(null);
      setPhoneNumberIsSubmitting(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initialTagGroupId, initialText]);

  // Reset phone number error when step changes away from phone number step
  useEffect(() => {
    if (step !== SchedulingModalStep.PHONE_NUMBER_STEP) {
      setPhoneNumberError(null);
      setPhoneNumberIsSubmitting(false);
    }
  }, [step]);

  useEffect(() => {
    if (schedule && !isNow && !date) {
      const sortedDays = [...schedule.entries()].map((entry) => entry[0]).sort();
      if (sortedDays[0]) {
        setDate(sortedDays[0]);
      }
    }
  }, [schedule, isNow, date]);

  useEffect(() => {
    // Don't override phone verification or OTP steps
    if (step === SchedulingModalStep.PHONE_NUMBER_STEP || step === SchedulingModalStep.OTP_STEP) {
      return;
    }
    if (!schedulingEligibility.canSchedule && schedulingEligibility.reason !== 'loading') {
      setStep(SchedulingModalStep.NOT_ENOUGH_MINUTES);
    } else if (step === SchedulingModalStep.NOT_ENOUGH_MINUTES && schedulingEligibility.canSchedule) {
      setStep(getStepAfterPhoneVerification());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [schedulingEligibility, step, startOnFeelingStep]);

  if (!open) return null;

  // Render header based on current step
  const renderHeader = () => {
    switch (step) {
      case SchedulingModalStep.TIME_STEP:
        return <TimeStepHeader onExit={onExit} />;
      case SchedulingModalStep.FEELING_STEP:
        return <FeelingStepHeader listenerName={listenerName} listenerImage={listenerImage} onExit={onExit} />;
      case SchedulingModalStep.NOT_ENOUGH_MINUTES:
        return <NotEnoughMinutesHeader onExit={onExit} reason={schedulingBlockReason} />;
      case SchedulingModalStep.PHONE_NUMBER_STEP:
        return <PhoneNumberStepHeader onExit={onExit} />;
      case SchedulingModalStep.OTP_STEP:
        return <OtpStepHeader onExit={onExit} />;
      default:
        return null;
    }
  };

  // Render footer based on current step
  const renderFooter = () => {
    switch (step) {
      case SchedulingModalStep.TIME_STEP:
        return (
          <div className="flex items-center justify-end gap-4">
            <button
              onClick={() => onExit()}
              className="text-primary bg-button-secondary flex items-center justify-center gap-1.5 rounded-full px-7 py-2.5 text-base font-medium leading-5 shadow-[0px_1px_0.5px_0.05px_rgba(29,41,61,0.02)]"
            >
              Cancel
            </button>
            <button
              data-testid="schedule-next-button"
              disabled={!time || !date}
              onClick={() => setStep(SchedulingModalStep.FEELING_STEP)}
              className="bg-button-primary flex items-center justify-center gap-1.5 rounded-full px-7 py-2.5 text-base font-medium leading-5 text-white shadow-[0px_1px_0.5px_0.05px_rgba(29,41,61,0.02)] disabled:opacity-50"
            >
              Next
            </button>
          </div>
        );
      case SchedulingModalStep.FEELING_STEP:
        return (
          <FeelingStepFooter
            isNowCall={startOnFeelingStep === 'Now'}
            onExit={onExit}
            setStep={setStep}
            moodScore={moodScore}
            selectedTagGroupId={selectedTagGroupId}
            sendRequest={sendRequest}
            listenerId={listenerId}
          />
        );
      case SchedulingModalStep.NOT_ENOUGH_MINUTES:
        return (
          <div className="flex items-center justify-end gap-4">
            <button
              onClick={() => onExit()}
              className="text-primary bg-button-secondary flex items-center justify-center gap-1.5 rounded-full px-7 py-2.5 text-base font-medium leading-5 shadow-[0px_1px_0.5px_0.05px_rgba(29,41,61,0.02)]"
            >
              Cancel
            </button>
            <button
              onClick={() => {
                const fullPath = history.location.pathname + history.location.search;
                sessionStorage.setItem('redirectFromPaymentMethodTo', fullPath);
                openSettingsModal(SettingsPage.paymentMethod);
              }}
              className="bg-button-primary flex items-center justify-center gap-1.5 rounded-full px-7 py-2.5 text-base font-medium leading-5 text-white shadow-[0px_1px_0.5px_0.05px_rgba(29,41,61,0.02)]"
            >
              {schedulingBlockReason === 'payment_failed' ? 'Update Payment Method' : 'Add Payment Method'}
            </button>
          </div>
        );
      case SchedulingModalStep.PHONE_NUMBER_STEP:
        return (
          <PhoneNumberStepFooter
            onExit={onExit}
            phoneNumber={phoneNumber}
            setStep={setStep}
            error={phoneNumberError}
            setError={setPhoneNumberError}
            isSubmitting={phoneNumberIsSubmitting}
            setIsSubmitting={setPhoneNumberIsSubmitting}
          />
        );
      case SchedulingModalStep.OTP_STEP:
        // TODO: extract OTP step footer element from OtpVerification component, or ensure OTP step displays as drawer
        return null; // OTP step handles its own buttons via OtpVerification component
      default:
        return null;
    }
  };

  return (
    <DrawerModal open={open} onClose={onExit} header={renderHeader()} footer={renderFooter()}>
      {step === SchedulingModalStep.TIME_STEP && (
        <TimeStepContent
          userTimeZone={userTimeZone}
          now={nowInUserTimeZone}
          schedule={schedule}
          date={date}
          setDate={setDate}
          time={time}
          setTime={setTime}
          isNow={!!isNow}
          initialTime={initialTime}
        />
      )}
      {step === SchedulingModalStep.FEELING_STEP && (
        <FeelingStepContent
          moodScore={moodScore}
          setMoodScore={setMoodScore}
          listenerName={listenerName}
          selectedTagGroupId={selectedTagGroupId}
          setSelectedTagGroupId={setSelectedTagGroupId}
          optionalText={optionalText}
          setOptionalText={setOptionalText}
        />
      )}
      {step === SchedulingModalStep.NOT_ENOUGH_MINUTES && (
        <NotEnoughMinutesContent
          ratePerMinute={
            user?.caller_role?.payment_data?.rate_per_minute != null
              ? (user.caller_role.payment_data.rate_per_minute / 100).toFixed(2)
              : undefined
          }
          availableMinutes={availableMinutes}
          reason={schedulingBlockReason}
        />
      )}
      {step === SchedulingModalStep.PHONE_NUMBER_STEP && (
        <PhoneNumberStepContent
          phoneNumber={phoneNumber}
          setPhoneNumber={setPhoneNumber}
          error={phoneNumberError}
          setError={setPhoneNumberError}
        />
      )}
      {step === SchedulingModalStep.OTP_STEP && (
        <OtpStepContent setStep={setStep} phoneNumberToVerify={phoneNumber} startOnFeelingStep={startOnFeelingStep} />
      )}
    </DrawerModal>
  );
};

// Calendar icon component (placeholder - you may need to import an actual icon)
const CalendarIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M19 4H5C3.89 4 3 4.9 3 6V20C3 21.1 3.89 22 5 22H19C20.1 22 21 21.1 21 20V6C21 4.9 20.1 4 19 4ZM19 20H5V9H19V20Z"
      fill="#101828"
    />
    <path d="M7 11H9V13H7V11Z" fill="#101828" />
    <path d="M11 11H13V13H11V11Z" fill="#101828" />
    <path d="M15 11H17V13H15V11Z" fill="#101828" />
  </svg>
);

// Close icon component
const CloseIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M15 5L5 15M5 5L15 15" stroke="#101828" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

// Header for Step 1
const TimeStepHeader: React.FC<{ onExit: () => void }> = ({ onExit }) => (
  <div className="flex items-center justify-between">
    <div className="flex items-center gap-2">
      <CalendarIcon className="h-6 w-6 shrink-0" />
      <p className="font-albert-sans text-label-gray text-xl font-medium leading-7">Schedule a call</p>
    </div>
    <button
      onClick={onExit}
      className="flex h-9 w-9 items-center justify-center rounded-full bg-gray-50"
      data-testid="schedule-close-button"
    >
      <CloseIcon className="h-5 w-5" />
    </button>
  </div>
);

const TimePill: React.FC<{
  selected?: boolean;
  onClick?: () => void;
  className?: string;
  children: React.ReactNode;
}> = ({ children, selected = false, onClick, className }) => (
  <button
    onClick={onClick}
    className={twMerge(
      selected ? 'bg-accent text-white' : 'text-primary bg-button-secondary hover:bg-button-secondary-hover',
      'flex h-10 items-center justify-center gap-1.5 whitespace-nowrap rounded-full px-7 py-2.5 text-base font-medium leading-5 shadow-[0px_1px_0.5px_0.05px_rgba(29,41,61,0.02)]',
      className,
    )}
  >
    {children}
  </button>
);

// Step 1: Date and Time Selection - Content Only
const TimeStepContent: React.FC<{
  now: Moment;
  isNow: boolean;
  schedule?: Map<number, Schedule>;
  date?: number;
  setDate: React.Dispatch<React.SetStateAction<number | undefined>>;
  time?: Moment;
  setTime: React.Dispatch<React.SetStateAction<Moment | undefined>>;
  userTimeZone: string;
  initialTime?: Moment;
}> = ({ now, time, setTime, isNow, date, schedule, setDate, userTimeZone, initialTime }) => {
  const timezoneAbbr = useMemo(() => {
    if (!userTimeZone) return '';
    try {
      return new Intl.DateTimeFormat('en-US', { timeZoneName: 'short', timeZone: userTimeZone }).format().split(' ')[1];
    } catch {
      return '';
    }
  }, [userTimeZone]);

  return (
    <div className="px-6 py-6">
      <div className="flex flex-col gap-6">
        {/* Date Selection */}
        <div className="flex flex-col gap-3">
          <div className="flex items-start justify-between">
            <p className="font-albert-sans text-label-gray text-base font-medium leading-5">Choose a day</p>
            {date && schedule?.get(date)?.dateLabel && (
              <p className="font-albert-sans text-base font-medium leading-5 text-stone-500">
                {schedule.get(date)?.dateLabel}
              </p>
            )}
          </div>
          <HorizontalScrollSection className="p-0">
            {schedule &&
              [...schedule.keys()].sort().map((scheduleKey) => (
                <div key={scheduleKey} data-testid={`select-date-${scheduleKey}`}>
                  <TimePill
                    selected={date === scheduleKey}
                    onClick={() => {
                      if (date !== scheduleKey) {
                        setTime(undefined);
                      }
                      setDate(scheduleKey);
                    }}
                  >
                    {schedule.get(scheduleKey)?.dateLabel}
                  </TimePill>
                </div>
              ))}
          </HorizontalScrollSection>
        </div>

        {/* Time Selection */}
        <div className="flex flex-1 flex-col gap-2.5">
          <div className="flex items-start justify-between">
            <p className="font-albert-sans text-label-gray text-base font-medium leading-5">
              Available Times {timezoneAbbr && `(${timezoneAbbr})`}
            </p>
            <p className="font-albert-sans text-base font-medium leading-5 text-stone-500">
              {time?.isSame(now, 'minute') ? 'Now' : time?.format('h:mm a')}
            </p>
          </div>
          <div className="grid grid-cols-2 gap-2 md:flex md:flex-row md:flex-wrap">
            {schedule &&
              date &&
              (() => {
                const daySchedule = schedule.get(date);
                const filteredData = daySchedule?.data.filter((entry) => entry.start.isSameOrAfter(now)) || [];
                const sortedData = filteredData.sort((date1, date2) =>
                  date1.start.isSameOrBefore(date2.start) ? -1 : 1,
                );

                return sortedData.map((scheduleData, index) => {
                  const isSelected = time !== undefined && scheduleData.start.isSame(time, 'minute');
                  return (
                    <div key={index} data-testid={`select-time-${index}`}>
                      <TimePill
                        onClick={() => setTime(scheduleData.start)}
                        selected={isSelected}
                        className="w-full md:w-auto"
                      >
                        {scheduleData.start.isSame(now) ? 'Now' : scheduleData.start.tz(userTimeZone).format('h:mm a')}
                      </TimePill>
                    </div>
                  );
                });
              })()}
          </div>
        </div>
      </div>
    </div>
  );
};

// Header for Step 2
const FeelingStepHeader: React.FC<{
  listenerName: string;
  listenerImage?: string;
  onExit: () => void;
}> = ({ listenerName, listenerImage, onExit }) => (
  <div className="flex items-center justify-between">
    <div className="flex items-center gap-2">
      {listenerImage && <Avatar imageUrl={listenerImage} className="h-8 w-8 shrink-0" />}
      <p className="font-albert-sans text-label-gray text-xl font-medium leading-7">Schedule a call</p>
    </div>
    <button
      onClick={onExit}
      className="flex h-9 w-9 items-center justify-center rounded-full bg-gray-50"
      data-testid="schedule-close-button"
    >
      <CloseIcon className="h-5 w-5" />
    </button>
  </div>
);

// Step 2: Feeling, Topic, and Text - Content Only
const FeelingStepContent: React.FC<{
  moodScore: number | null;
  setMoodScore: React.Dispatch<React.SetStateAction<number | null>>;
  listenerName: string;
  selectedTagGroupId?: number;
  setSelectedTagGroupId: React.Dispatch<React.SetStateAction<number | undefined>>;
  optionalText: string;
  setOptionalText: React.Dispatch<React.SetStateAction<string>>;
}> = ({
  listenerName,
  selectedTagGroupId,
  setSelectedTagGroupId,
  moodScore,
  setMoodScore,
  optionalText,
  setOptionalText,
}) => {
  const { data: topicTagGroups } = useTagGroups();

  return (
    <div className="px-6 py-6">
      <div className="flex flex-col gap-6">
        {/* Feeling Selection – 1 to 10 scale */}
        <div className="flex flex-col gap-2.5">
          <div className="flex flex-col gap-2">
            <p className="font-albert-sans text-label-gray text-base font-medium leading-5">How are you feeling?</p>
            <p className="font-albert-sans text-body-gray text-sm font-normal leading-5">
              Let {listenerName} know how you are feeling.
            </p>
          </div>
          <div className="flex flex-col gap-3">
            <div className="flex flex-wrap items-center gap-2.5 md:gap-2">
              {Array.from({ length: 10 }, (_, idx) => {
                const value = idx + 1;
                const selected = moodScore === value;
                return (
                  <button
                    key={value}
                    type="button"
                    onClick={() => setMoodScore(value)}
                    className={twMerge(
                      selected ? 'bg-accent text-white' : 'bg-button-secondary text-muted-text-gray',
                      'flex h-6 w-6 items-center justify-center rounded-full text-sm font-medium shadow-[0px_1px_0.5px_0.05px_rgba(29,41,61,0.02)] md:h-9 md:w-9',
                    )}
                  >
                    {value}
                  </button>
                );
              })}
            </div>
            <div className="text-muted-text-gray flex w-[320px] justify-between text-xs font-medium md:grid md:w-full md:grid-cols-10">
              <span className="md:col-start-1 md:justify-self-start">Very Low</span>
              <span className="md:col-start-6 md:justify-self-start">Very Positive</span>
            </div>
          </div>
        </div>

        {/* Topic Selection */}
        <div className="flex flex-1 flex-col gap-2.5">
          <p className="font-albert-sans text-label-gray text-base font-medium leading-5">
            What would you like to talk about?
          </p>
          <TopicFilterDropdown
            label="Choose a topic"
            placeholder="Choose a topic"
            selectedTagGroupId={selectedTagGroupId}
            onSelect={setSelectedTagGroupId}
            allOptionLabel="Choose a topic"
            topicTagGroups={topicTagGroups}
          />
        </div>

        {/* Optional Text Area */}
        <div className="flex flex-col gap-2.5">
          <div className="flex items-center gap-1">
            <p className="font-albert-sans text-label-gray text-base font-medium leading-5">
              How can your Peer best support you during this call?
            </p>
          </div>
          <textarea
            value={optionalText}
            onChange={(e) => setOptionalText(e.target.value)}
            placeholder="Providing a bit of context here helps your Peer arrive prepared to focus on what matters most to you."
            className="font-albert-sans bg-button-secondary border-input-border-gray text-placeholder-gray placeholder:text-placeholder-gray min-h-[203px] w-full rounded-[12px] border p-3.5 text-sm font-normal leading-5 shadow-[0px_1px_0.5px_0.05px_rgba(29,41,61,0.02)] focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
      </div>
    </div>
  );
};

// Footer for Step 2
const FeelingStepFooter: React.FC<{
  isNowCall: boolean;
  onExit: () => void;
  setStep: React.Dispatch<React.SetStateAction<SchedulingModalStep>>;
  moodScore: number | null;
  selectedTagGroupId?: number;
  sendRequest: () => Promise<void>;
  listenerId: number;
}> = ({ isNowCall, onExit, setStep, moodScore, selectedTagGroupId, sendRequest, listenerId }) => {
  const queryClient = useQueryClient();
  const [inProgress, setInProgress] = useState<boolean>(false);

  const handleSubmit = async () => {
    try {
      setInProgress(true);
      await sendRequest();
    } finally {
      queryClient.invalidateQueries(peersQueryKeys.peerDetail(listenerId));
      queryClient.invalidateQueries(['peersByTopic']);
      setInProgress(false);
    }
  };

  return (
    <div className="flex items-center justify-end gap-4">
      {!isNowCall && (
        <button
          onClick={() => setStep(SchedulingModalStep.TIME_STEP)}
          disabled={inProgress}
          className="text-primary bg-button-secondary flex items-center justify-center gap-1.5 rounded-full px-7 py-2.5 text-base font-medium leading-5 shadow-[0px_1px_0.5px_0.05px_rgba(29,41,61,0.02)] disabled:opacity-50"
        >
          Back
        </button>
      )}
      {isNowCall && (
        <button
          onClick={() => onExit()}
          disabled={inProgress}
          className="text-primary bg-button-secondary flex items-center justify-center gap-1.5 rounded-full px-7 py-2.5 text-base font-medium leading-5 shadow-[0px_1px_0.5px_0.05px_rgba(29,41,61,0.02)] disabled:opacity-50"
        >
          Cancel
        </button>
      )}
      <button
        data-testid="schedule-call-button"
        disabled={moodScore === null || !selectedTagGroupId || inProgress}
        onClick={handleSubmit}
        className={twMerge(
          'flex items-center justify-center gap-1.5 rounded-full px-7 py-2.5 text-base font-medium leading-5 text-white shadow-[0px_1px_0.5px_0.05px_rgba(29,41,61,0.02)] disabled:opacity-50',
          isNowCall ? 'bg-success' : 'bg-button-primary',
        )}
      >
        {isNowCall && (
          <div className="flex h-5 w-5 shrink-0 items-center justify-center">
            <img src={OutgoingCallIcon} alt="Start call" className="h-5 w-5" />
          </div>
        )}
        {inProgress ? 'Scheduling...' : isNowCall ? 'Start Call' : 'Schedule Call'}
      </button>
    </div>
  );
};

// Header for Not Enough Minutes Step
const NotEnoughMinutesHeader: React.FC<{ onExit: () => void; reason?: SchedulingBlockReason }> = ({
  onExit,
  reason,
}) => (
  <div className="flex items-center justify-between">
    <div className="flex items-center gap-2">
      <p className="font-albert-sans text-label-gray text-xl font-medium leading-7">
        {reason === 'payment_failed' ? 'Payment method issue' : 'Low call minutes available'}
      </p>
    </div>
    <button
      onClick={onExit}
      className="flex h-9 w-9 items-center justify-center rounded-full bg-gray-50"
      data-testid="schedule-close-button"
    >
      <CloseIcon className="h-5 w-5" />
    </button>
  </div>
);

// Not Enough Minutes Step - Content Only
const NotEnoughMinutesContent: React.FC<{
  availableMinutes?: number;
  ratePerMinute?: string | number;
  reason?: SchedulingBlockReason;
}> = ({ availableMinutes, ratePerMinute, reason }) => {
  const hasPartialCredit =
    availableMinutes !== undefined && availableMinutes > 0 && availableMinutes < MIN_SCHEDULING_MINUTES;

  return (
    <div className="px-6 py-6">
      <div className="flex flex-col gap-6">
        <div className="flex flex-col gap-4">
          <div className="font-albert-sans text-body-gray flex flex-col gap-4 text-sm font-medium leading-5">
            {reason === 'payment_failed' ? (
              <span>
                There was an error processing your last payment. Please update your payment method prior to scheduling a
                new call.
              </span>
            ) : (
              <>
                <span>
                  To schedule a call, you need at least {MIN_SCHEDULING_MINUTES} minutes of credit or a payment method
                  on file.
                </span>
                {hasPartialCredit && (
                  <span>
                    You have {availableMinutes} {availableMinutes === 1 ? 'minute' : 'minutes'} remaining — you need at
                    least {MIN_SCHEDULING_MINUTES} to schedule without a payment method.
                  </span>
                )}
                <span>
                  We will use your available credit minutes, and then charge your payment method for any additional
                  minutes if necessary.
                </span>
              </>
            )}
          </div>
          {ratePerMinute != null && (
            <div className="font-albert-sans text-label-gray text-sm font-bold leading-5">
              Rate per minute: ${ratePerMinute}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Header for Phone Number Step
const PhoneNumberStepHeader: React.FC<{ onExit: () => void }> = ({ onExit }) => (
  <div className="flex items-center justify-between">
    <div className="flex items-center gap-2">
      <p className="font-albert-sans text-label-gray text-xl font-medium leading-7">Verify Your Phone Number</p>
    </div>
    <button
      onClick={onExit}
      className="flex h-9 w-9 items-center justify-center rounded-full bg-gray-50"
      data-testid="schedule-close-button"
    >
      <CloseIcon className="h-5 w-5" />
    </button>
  </div>
);

// Phone Number Step - Content Only
const PhoneNumberStepContent: React.FC<{
  phoneNumber: string;
  setPhoneNumber: React.Dispatch<React.SetStateAction<string>>;
  error: string | null;
  setError: React.Dispatch<React.SetStateAction<string | null>>;
}> = ({ phoneNumber, setPhoneNumber, error, setError }) => {
  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setPhoneNumber(formatPhoneNumber(value));
    setError(null);
  };

  return (
    <div className="px-6 py-6">
      <div className="flex flex-col gap-6">
        <div className="flex flex-col gap-2">
          <p className="font-albert-sans text-label-gray text-base font-medium leading-5">
            We need to verify your phone number to schedule this call. This helps ensure secure communication.
          </p>
        </div>

        <div className="flex flex-col gap-2.5">
          <label htmlFor="phone-number" className="font-albert-sans text-label-gray text-base font-medium leading-5">
            Phone Number
          </label>
          <input
            id="phone-number"
            type="tel"
            value={phoneNumber}
            onChange={handlePhoneChange}
            placeholder="(123) 456-7890"
            className="font-albert-sans bg-button-secondary text-label-gray border-input-border-gray placeholder:text-placeholder-gray h-12 w-full max-w-[280px] rounded-[12px] border px-3.5 text-sm font-normal leading-5 shadow-[0px_1px_0.5px_0.05px_rgba(29,41,61,0.02)] focus:outline-none focus:ring-2 focus:ring-blue-500"
            maxLength={14}
            data-testid="phone-number-input"
          />
          {error && <p className="font-albert-sans text-sm font-normal leading-5 text-red-500">{error}</p>}
        </div>
      </div>
    </div>
  );
};

// Footer for Phone Number Step
const PhoneNumberStepFooter: React.FC<{
  onExit: () => void;
  phoneNumber: string;
  setStep: React.Dispatch<React.SetStateAction<SchedulingModalStep>>;
  error: string | null;
  setError: React.Dispatch<React.SetStateAction<string | null>>;
  isSubmitting: boolean;
  setIsSubmitting: React.Dispatch<React.SetStateAction<boolean>>;
}> = ({ onExit, phoneNumber, setStep, error, setError, isSubmitting, setIsSubmitting }) => {
  const { mutate: sendKeycloakVerificationCode } = useSendKeycloakVerificationCode();

  const handleSubmit = () => {
    if (!phoneNumber || phoneNumber.replace(/\D/g, '').length !== 10) {
      setError('Please enter a valid 10-digit phone number');
      return;
    }

    setIsSubmitting(true);
    setError(null);

    sendKeycloakVerificationCode(
      { mobilePhone: phoneNumber.replace(/\D/g, '') },
      {
        onSuccess: () => {
          setStep(SchedulingModalStep.OTP_STEP);
          setIsSubmitting(false);
        },
        onError: (error: any) => {
          setError(error.response?.data?.description || 'Failed to send verification code');
          setIsSubmitting(false);
        },
      },
    );
  };

  return (
    <div className="flex items-center justify-end gap-4">
      <button
        onClick={() => onExit()}
        className="text-primary bg-button-secondary flex items-center justify-center gap-1.5 rounded-full px-7 py-2.5 text-base font-medium leading-5 shadow-[0px_1px_0.5px_0.05px_rgba(29,41,61,0.02)]"
      >
        Cancel
      </button>
      <button
        onClick={handleSubmit}
        className="bg-button-primary flex items-center justify-center gap-1.5 rounded-full px-7 py-2.5 text-base font-medium leading-5 text-white shadow-[0px_1px_0.5px_0.05px_rgba(29,41,61,0.02)] disabled:opacity-50"
        disabled={isSubmitting || !phoneNumber || phoneNumber.replace(/\D/g, '').length !== 10}
        data-testid="phone-number-continue-button"
      >
        {isSubmitting ? 'Saving...' : 'Continue'}
      </button>
    </div>
  );
};

// Header for OTP Step
const OtpStepHeader: React.FC<{ onExit: () => void }> = ({ onExit }) => (
  <div className="flex items-center justify-between">
    <div className="flex items-center gap-2">
      <p className="font-albert-sans text-label-gray text-xl font-medium leading-7">Enter Verification Code</p>
    </div>
    <button
      onClick={onExit}
      className="flex h-9 w-9 items-center justify-center rounded-full bg-gray-50"
      data-testid="schedule-close-button"
    >
      <CloseIcon className="h-5 w-5" />
    </button>
  </div>
);

// OTP Step - Content Only
const OtpStepContent: React.FC<{
  setStep: React.Dispatch<React.SetStateAction<SchedulingModalStep>>;
  phoneNumberToVerify: string;
  startOnFeelingStep?: 'Now' | 'Time' | undefined;
}> = ({ setStep, phoneNumberToVerify, startOnFeelingStep = undefined }) => {
  const { mutate: sendKeycloakVerificationCode } = useSendKeycloakVerificationCode();
  const { mutate: verifyKeycloakVerificationCode } = useVerifyKeycloakVerificationCode();
  const [otpValue, setOtpValue] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const resendVerificationCode = () => {
    if (!phoneNumberToVerify) return;

    setIsResending(true);
    setError(null);

    sendKeycloakVerificationCode(
      { mobilePhone: phoneNumberToVerify!.replace(/\D/g, '') },
      {
        onSuccess: () => {
          Toast.success('Verification code sent successfully');
          setIsResending(false);
        },
        onError: (error: any) => {
          setError(error.response?.data?.description || 'Failed to send verification code');
          setIsResending(false);
        },
      },
    );
  };

  const handleSubmit = () => {
    if (otpValue.length !== 6) {
      setError('Please enter the 6-digit verification code');
      return;
    }

    setIsSubmitting(true);
    setError(null);

    verifyKeycloakVerificationCode(
      {
        verificationCode: otpValue,
      },
      {
        onSuccess: () => {
          Toast.success('Phone number verified successfully');
          setStep(
            startOnFeelingStep === 'Now' || startOnFeelingStep === 'Time'
              ? SchedulingModalStep.FEELING_STEP
              : SchedulingModalStep.TIME_STEP,
          );
          setIsSubmitting(false);
        },
        onError: (error: any) => {
          setError(error.response?.data?.message || 'Invalid verification code. Please try again.');
          setOtpValue('');
          setIsSubmitting(false);
        },
      },
    );
  };

  return (
    <div className="px-6 py-6">
      <div className="flex flex-col gap-6">
        <div className="flex flex-col gap-2">
          <p className="font-albert-sans text-label-gray text-base font-medium leading-5">
            We sent a verification code to {phoneNumberToVerify}
          </p>
        </div>

        <div className="flex flex-col items-center gap-6">
          <OtpVerification
            value={otpValue}
            onChange={(value) => {
              setOtpValue(value);
              if (error) {
                setError(null);
              }
            }}
            onSubmit={handleSubmit}
            onResend={resendVerificationCode}
            onCancel={() => setStep(SchedulingModalStep.PHONE_NUMBER_STEP)}
            isSubmitting={isSubmitting}
            isResending={isResending}
            error={error}
            showCancelButton={true}
            submitButtonText="Continue"
            cancelButtonText="Back"
            submitButtonClassName="flex-1 rounded-full px-7 py-2.5 text-base font-medium leading-5 text-white shadow-[0px_1px_0.5px_0.05px_rgba(29,41,61,0.02)] disabled:opacity-50 bg-button-primary"
            containerStyle="flex justify-center w-full"
          />
        </div>
      </div>
    </div>
  );
};
